import React, { useEffect, useRef, useState } from "react";
import { api, mediaSrcById } from "../api";
import { useApp } from "../context/AppContext";
import { resizeToSquareJpeg } from "../lib/imageResize";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Building2, Copy, RefreshCw, QrCode, Share2, Link2, Loader2, Pencil,
  Phone, Mail, MapPin, Globe, Camera, Unlink, Eye,
} from "lucide-react";
import { shareLink } from "../lib/share";

const ContactRow = ({ icon: Icon, value, href, testid }) => {
  if (!value) return null;
  const inner = (
    <span className="flex items-center gap-2 min-w-0">
      <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
      <span className="text-xs truncate">{value}</span>
    </span>
  );
  return (
    <div className="py-0.5" data-testid={testid}>
      {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="hover:underline">{inner}</a> : inner}
    </div>
  );
};

/** A company shown as a BUSINESS CARD (logo + name + contact info). */
export const CompanyBusinessCard = ({ c, t, mine = false, footer = null, children }) => (
  <Card className={`relative overflow-hidden p-0 ${mine ? "border-primary/40" : ""}`}
    data-testid={mine ? "company-card-mine" : `company-card-${c.id}`}>
    <div className={`h-1.5 w-full ${mine ? "bg-primary" : "bg-muted-foreground/20"}`} />
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 rounded-xl border bg-white flex items-center justify-center overflow-hidden shrink-0">
          {c.logo_base64 ? (
            <img src={`data:image/jpeg;base64,${c.logo_base64}`} alt="" className="h-full w-full object-contain" data-testid="company-card-logo" />
          ) : (
            <Building2 className="h-7 w-7 text-muted-foreground/50" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-base truncate" data-testid="company-card-name">{c.name}</h3>
            {mine && <Badge className="text-[9px] shrink-0">{t("my_company_badge")}</Badge>}
          </div>
          <div className="mt-1">
            <ContactRow icon={Phone} value={c.phone} href={c.phone ? `tel:${c.phone}` : null} testid="company-card-phone" />
            <ContactRow icon={Mail} value={c.email} href={c.email ? `mailto:${c.email}` : null} testid="company-card-email" />
            <ContactRow icon={MapPin} value={c.address} testid="company-card-address" />
            <ContactRow icon={Globe} value={c.website}
              href={c.website ? (c.website.startsWith("http") ? c.website : `https://${c.website}`) : null}
              testid="company-card-website" />
            {!c.phone && !c.email && !c.address && !c.website && (
              <p className="text-[11px] text-muted-foreground italic">{t("company_card_empty")}</p>
            )}
          </div>
        </div>
        {children}
      </div>
      {footer}
    </div>
  </Card>
);

/**
 * Admin Panel → "Empresa" tab.
 * My company as an editable BUSINESS CARD + one card per connected company
 * (exactly like exchanging business cards) with a discreet "Disconnect".
 */
export const CompanyCardsTab = ({ t }) => {
  const { user } = useApp();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const [partnerBusy, setPartnerBusy] = useState(false);
  const [disconnect, setDisconnect] = useState(null); // partner pending confirm
  // partner contact visibility (which partner contacts MY users can see)
  const [visPartner, setVisPartner] = useState(null); // {id, name} — modal open when set
  const [visTab, setVisTab] = useState("in"); // in = partner contacts my team sees | out = MY contacts they see
  const [visMembers, setVisMembers] = useState([]);
  const [visHidden, setVisHidden] = useState([]); // hidden user ids (partner users)
  const [visMyMembers, setVisMyMembers] = useState([]);
  const [visBlocked, setVisBlocked] = useState([]); // MY user ids hidden from the partner
  const [visLoading, setVisLoading] = useState(false);
  const [visSaving, setVisSaving] = useState(false);
  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", website: "", logo_base64: "" });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/company/mine");
      setCompany(data.company);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = () => {
    setForm({
      name: company?.name || "", phone: company?.phone || "", email: company?.email || "",
      address: company?.address || "", website: company?.website || "", logo_base64: company?.logo_base64 || "",
    });
    setEditOpen(true);
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const b64 = await resizeToSquareJpeg(file, 320, 0.85);
      setForm((f) => ({ ...f, logo_base64: b64 }));
    } catch (err) { toast.error(t("something_wrong")); }
  };

  const saveCard = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/company/mine", form);
      setCompany((c) => ({ ...c, ...(data.company || {}) }));
      setEditOpen(false);
      toast.success(t("company_card_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setSaving(false); }
  };

  const openVisibility = async (partner) => {
    setVisPartner(partner);
    setVisTab("in");
    setVisLoading(true);
    setVisMembers([]);
    setVisHidden([]);
    setVisMyMembers([]);
    setVisBlocked([]);
    try {
      const [inbound, outbound] = await Promise.all([
        api.get(`/company/partner/${partner.id}/visibility`),
        api.get(`/company/partner/${partner.id}/shared-visibility`),
      ]);
      setVisMembers(inbound.data.members || []);
      setVisHidden(inbound.data.hidden_user_ids || []);
      setVisMyMembers(outbound.data.members || []);
      setVisBlocked(outbound.data.blocked_user_ids || []);
    } catch (e) {
      toast.error(t("something_wrong"));
      setVisPartner(null);
    } finally { setVisLoading(false); }
  };

  const toggleVisible = (uid) => {
    setVisHidden((h) => (h.includes(uid) ? h.filter((x) => x !== uid) : [...h, uid]));
  };
  const toggleBlocked = (uid) => {
    setVisBlocked((h) => (h.includes(uid) ? h.filter((x) => x !== uid) : [...h, uid]));
  };

  const saveVisibility = async () => {
    if (!visPartner) return;
    setVisSaving(true);
    try {
      await Promise.all([
        api.put(`/company/partner/${visPartner.id}/visibility`, { hidden_user_ids: visHidden }),
        api.put(`/company/partner/${visPartner.id}/shared-visibility`, { blocked_user_ids: visBlocked }),
      ]);
      toast.success(t("partner_visibility_saved"));
      setVisPartner(null);
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally { setVisSaving(false); }
  };

  const connectPartner = async (e) => {
    e.preventDefault();
    if (!partnerCode.trim()) return;
    setPartnerBusy(true);
    try {
      const { data } = await api.post("/company/partner", { code: partnerCode.trim().toUpperCase() });
      toast.success(`${t("partner_connected_toast")} — ${data.partner?.name}`);
      setPartnerCode("");
      load();
      // Right away: choose which of THEIR contacts my users can see
      if (data.partner?.id) openVisibility(data.partner);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "invalid_company_code") toast.error(t("invalid_company_code"));
      else if (detail === "own_company") toast.error(t("own_company_code"));
      else toast.error(t("something_wrong"));
    } finally { setPartnerBusy(false); }
  };

  const doDisconnect = async (purge) => {
    const p = disconnect;
    setDisconnect(null);
    if (!p) return;
    try {
      await api.post("/company/partner/remove", { company_id: p.id, purge });
      toast.success(`${t("partner_disconnected_toast")} — ${p.name}`);
      load();
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const regenerateCode = async () => {
    try {
      const { data } = await api.post("/company/regenerate-code");
      setCompany((c) => ({ ...c, join_code: data.join_code }));
      toast.success(t("code_regenerated_toast"));
    } catch (e) { toast.error(t("something_wrong")); }
  };

  if (loading) return <div className="h-40 bg-muted rounded-xl animate-pulse" />;
  if (!company) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="company-tab-empty">{t("no_company")}</p>;

  return (
    <div className="space-y-4" data-testid="admin-company-tab">
      {/* ---- my business card ---- */}
      <CompanyBusinessCard
        c={company} t={t} mine
        footer={(
          <div className="mt-3 space-y-2">
            {company.join_code && (
              <div className="flex items-center gap-2 flex-wrap rounded-lg border bg-muted/40 px-3 py-2">
                <span className="text-[11px] text-muted-foreground">{t("company_code")}:</span>
                <span className="font-mono-plex font-bold tracking-[0.25em] text-[#2B3990]" data-testid="admin-company-join-code">{company.join_code}</span>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2" data-testid="admin-copy-company-code"
                  onClick={() => { try { navigator.clipboard.writeText(company.join_code); toast.success(t("copied")); } catch (e) {} }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground" data-testid="admin-regenerate-company-code" onClick={regenerateCode}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-7 px-2" data-testid="admin-company-qr-btn" onClick={() => setQrOpen(true)}>
                  <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                </Button>
                <span className="text-[10px] text-muted-foreground w-full">{t("company_code_hint")}</span>
              </div>
            )}
          </div>
        )}
      >
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Button type="button" variant="outline" size="sm" className="h-8" data-testid="edit-company-card-btn" onClick={openEdit}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> {t("edit_company_card")}
          </Button>
          {typeof company.members === "number" && (
            <Badge variant="secondary" className="text-[10px]">{company.members} {t("members")}</Badge>
          )}
        </div>
      </CompanyBusinessCard>

      {/* ---- connect a company ---- */}
      <Card className="p-3" data-testid="connect-company-card">
        <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5 text-primary" /> {t("connect_companies")}
        </p>
        <p className="text-[11px] text-muted-foreground mb-2">{t("connect_company_hint")}</p>
        <form onSubmit={connectPartner} className="flex items-center gap-2">
          <Input
            data-testid="admin-partner-code-input"
            placeholder={t("partner_code_placeholder")}
            value={partnerCode}
            onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
            className="h-9 text-xs font-mono-plex uppercase tracking-widest"
            maxLength={10}
          />
          <Button type="submit" size="sm" variant="outline" className="h-9" data-testid="admin-partner-connect-btn" disabled={partnerBusy || !partnerCode.trim()}>
            {partnerBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />} {t("connect")}
          </Button>
        </form>
      </Card>

      {/* ---- partner business cards ---- */}
      <div>
        <div className="flex items-center gap-3 py-1.5 px-1 mb-2">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("partner_companies")}</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        {(company.partners || []).length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4" data-testid="no-partners-note">{t("no_partner_companies")}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(company.partners || []).map((p) => (
            <CompanyBusinessCard key={p.id} c={p} t={t}
              footer={(
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    data-testid={`partner-visibility-btn-${p.id}`}
                    onClick={() => openVisibility(p)}
                    className="text-[11px] text-muted-foreground hover:text-primary underline-offset-2 hover:underline flex items-center gap-1"
                  >
                    <Eye className="h-3 w-3" /> {t("partner_visibility_btn")}
                  </button>
                  <button
                    type="button"
                    data-testid={`disconnect-company-btn-${p.id}`}
                    onClick={() => setDisconnect(p)}
                    className="text-[11px] text-muted-foreground hover:text-destructive underline-offset-2 hover:underline flex items-center gap-1"
                  >
                    <Unlink className="h-3 w-3" /> {t("disconnect_company")}
                  </button>
                </div>
              )}
            />
          ))}
        </div>
      </div>

      {/* ---- edit my card ---- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-company-card-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{t("edit_company_card")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="company-logo-pick-btn"
                onClick={() => logoRef.current?.click()}
                className="h-16 w-16 rounded-xl shrink-0 overflow-hidden border-2 border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors bg-white"
              >
                {form.logo_base64 ? (
                  <img src={`data:image/jpeg;base64,${form.logo_base64}`} alt="" className="h-full w-full object-contain" />
                ) : (
                  <Camera className="h-6 w-6" />
                )}
              </button>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="company-logo-file-input" onChange={onPickLogo} />
              <div className="flex-1 space-y-1">
                <Label className="text-xs">{t("company_name_label")}</Label>
                <Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                {form.logo_base64 && (
                  <button type="button" className="text-[11px] text-destructive underline underline-offset-2" data-testid="company-logo-remove-btn"
                    onClick={() => setForm((f) => ({ ...f, logo_base64: "" }))}>
                    {t("remove_photo")}
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t("phone")}</Label>
                <Input data-testid="company-phone-input" placeholder="(555) 123-4567" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("email")}</Label>
                <Input data-testid="company-email-input" placeholder="contato@empresa.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("company_address_label")}</Label>
              <Input data-testid="company-address-input" placeholder="123 Main St, Atlanta GA" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("company_website_label")}</Label>
              <Input data-testid="company-website-input" placeholder="www.empresa.com" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-company-card-btn" onClick={saveCard} disabled={saving || !form.name.trim()} className="w-full">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pencil className="h-4 w-4 mr-2" />} {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- QR invite (join the company) ---- */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl p-5 sm:p-6" aria-describedby={undefined} data-testid="admin-company-qr-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-center">{t("company_qr_title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 min-w-0">
            <div className="rounded-2xl border bg-white p-3 shadow-sm">
              <QRCodeSVG value={`${window.location.origin}/?join=${company.join_code}`} size={188} level="M" data-testid="admin-company-qr-svg" />
            </div>
            <div className="w-full min-w-0 text-center">
              <div className="font-mono-plex font-bold tracking-[0.3em] text-lg text-[#2B3990]">{company.join_code}</div>
              <p className="mt-1.5 text-[11px] text-muted-foreground text-center leading-snug break-words px-2">{t("company_qr_hint")}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 w-full pt-1">
              <Button type="button" variant="outline" className="h-10 w-full min-w-0 px-2 text-xs" data-testid="admin-company-qr-copy-link"
                onClick={() => { try { navigator.clipboard.writeText(`${window.location.origin}/?join=${company.join_code}`); toast.success(t("copied")); } catch (e) {} }}>
                <Copy className="h-3.5 w-3.5 mr-1.5 shrink-0" /> <span className="truncate">{t("company_qr_copy_link")}</span>
              </Button>
              <Button type="button" className="h-10 w-full min-w-0 px-2 text-xs" data-testid="admin-company-qr-share-link"
                onClick={async () => {
                  const link = `${window.location.origin}/?join=${company.join_code}`;
                  const result = await shareLink({ title: "OnSiteComms", text: t("share_invite_text_company"), url: link });
                  if (result === "copied") toast.success(t("link_copied"));
                  else if (result === "failed") toast.error(t("something_wrong"));
                }}>
                <Share2 className="h-3.5 w-3.5 mr-1.5 shrink-0" /> <span className="truncate">{t("share_link")}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- partner contact visibility (checkmarks) ---- */}
      <Dialog open={!!visPartner} onOpenChange={(v) => { if (!v) setVisPartner(null); }}>
        <DialogContent className="max-w-md" data-testid="partner-visibility-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" /> {t("partner_visibility_title")} — {visPartner?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground leading-relaxed -mt-1">
            {visTab === "in" ? t("partner_visibility_hint") : t("partner_visibility_hint_out")}
          </p>
          {/* direction tabs: what MY team sees x what THEY see of my company */}
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            <button
              type="button"
              data-testid="partner-visibility-tab-in"
              onClick={() => setVisTab("in")}
              className={`h-8 rounded-md text-[11px] font-semibold transition-colors ${visTab === "in" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t("partner_visibility_tab_in")}
            </button>
            <button
              type="button"
              data-testid="partner-visibility-tab-out"
              onClick={() => setVisTab("out")}
              className={`h-8 rounded-md text-[11px] font-semibold transition-colors ${visTab === "out" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t("partner_visibility_tab_out")}
            </button>
          </div>
          {visLoading ? (
            <div className="space-y-2 py-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />)}
            </div>
          ) : (() => {
            const activeMembers = visTab === "in" ? visMembers : visMyMembers;
            const activeHidden = visTab === "in" ? visHidden : visBlocked;
            const activeToggle = visTab === "in" ? toggleVisible : toggleBlocked;
            const setActiveHidden = visTab === "in" ? setVisHidden : setVisBlocked;
            return (
            <>
              <div className="flex items-center justify-between px-1">
                <span className="text-[11px] font-medium text-muted-foreground" data-testid="partner-visibility-count">
                  {t("partner_visibility_count")
                    .replace("{n}", String(activeMembers.length - activeHidden.length))
                    .replace("{t}", String(activeMembers.length))}
                </span>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="partner-visibility-select-all" onClick={() => setActiveHidden([])}
                    className="text-[11px] text-primary hover:underline underline-offset-2">
                    {t("select_all")}
                  </button>
                  <button type="button" data-testid="partner-visibility-clear-all" onClick={() => setActiveHidden(activeMembers.map((m) => m.id))}
                    className="text-[11px] text-muted-foreground hover:underline underline-offset-2">
                    {t("clear_all")}
                  </button>
                </div>
              </div>
              <div className="max-h-[45vh] overflow-y-auto -mx-2 px-2 space-y-0.5">
                {activeMembers.map((m) => {
                  const visible = !activeHidden.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      data-testid={`partner-visibility-row-${m.id}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${visible ? "hover:bg-muted/60" : "opacity-55 hover:opacity-80"}`}
                    >
                      <Checkbox
                        data-testid={`partner-visibility-check-${m.id}`}
                        checked={visible}
                        onCheckedChange={() => activeToggle(m.id)}
                      />
                      <Avatar className="h-9 w-9">
                        {m.avatar_media_id && (
                          <AvatarImage src={mediaSrcById(m.avatar_media_id)} alt="" className="object-cover" />
                        )}
                        <AvatarFallback style={{ backgroundColor: m.avatar_color || "#64748b", color: "#fff" }} className="text-[11px] font-semibold">
                          {(m.name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.name}</div>
                        {m.title && <div className="text-[11px] text-muted-foreground truncate">{m.title}</div>}
                      </div>
                      <Badge variant="secondary" className="text-[10px] shrink-0">{t(m.role)}</Badge>
                    </label>
                  );
                })}
                {activeMembers.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-6">—</p>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="partner-visibility-save" className="w-full" onClick={saveVisibility} disabled={visSaving}>
                  {visSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />} {t("save")}
                </Button>
              </DialogFooter>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ---- disconnect confirm: delete related convs/contacts OR keep as loose contacts ---- */}
      <AlertDialog open={!!disconnect} onOpenChange={(v) => { if (!v) setDisconnect(null); }}>
        <AlertDialogContent data-testid="disconnect-company-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("disconnect_company")} — {disconnect?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("disconnect_company_confirm")}
              <span className="block mt-2 font-medium text-foreground">{t("purge_related_question")}</span>
              <span className="block mt-1 text-[11px]">{t("purge_no_hint")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction
              data-testid="disconnect-company-purge"
              className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => doDisconnect(true)}
            >
              {t("purge_yes_delete_all")}
            </AlertDialogAction>
            <AlertDialogAction
              data-testid="disconnect-company-keep"
              className="w-full"
              onClick={() => doDisconnect(false)}
            >
              {t("purge_no_keep_loose")}
            </AlertDialogAction>
            <AlertDialogCancel data-testid="disconnect-company-cancel" className="w-full mt-0">{t("cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
